'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import type { OtpVerificationModalProps } from '@ui/components/features/otp';
import { getOtpDeliveryInfo, getOtpSendErrorKey, getOtpVerifyErrorKey } from '@website/utils/otp';
import { formatPhoneNumberForDisplay } from '@website/utils/phoneFormat';

import { useOtpDeliveryStatus, useSendOtp, useVerifyOtp } from './useOtp';
import { useOtpModalTranslations } from './useOtpModalTranslations';

export interface UseOtpVerificationFlowOptions {
  locale: string;
  /** Runs once the number is verified, before the modal closes. */
  onVerified: () => void;
  /** Runs after the modal closed because the user wants to correct the number (focus the phone field). */
  onChangeNumber: () => void;
}

export type OtpVerificationFlowModalProps = Omit<OtpVerificationModalProps, 'isMobile'>;

/**
 * Everything the phone-verification modal needs: sending and resending the code, the voice-call
 * fallback, delivery feedback, translated errors. The number is kept in E.164 for the
 * API; the modal only ever displays the human-formatted copy it is given.
 */
export function useOtpVerificationFlow({ locale, onVerified, onChangeNumber }: UseOtpVerificationFlowOptions) {
  const t = useTranslations('Otp');
  const [isOpen, setIsOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  const sendOtp = useSendOtp(locale);
  const verifyOtp = useVerifyOtp();
  // Only while the modal shows it: the page stays mounted after a close, and every check
  // spends the account-wide Twilio Attempts API budget.
  const { status, statusUnavailable } = useOtpDeliveryStatus(isOpen ? sendOtp.sendId : null);
  const translations = useOtpModalTranslations();

  const { mutate: sendCode, isCoolingDown } = sendOtp;
  const { mutateAsync: verifyCode } = verifyOtp;

  /** Opens the modal for this E.164 number and sends a code unless one just went out to it. */
  const start = useCallback(
    (e164PhoneNumber: string) => {
      setPhoneNumber(e164PhoneNumber);
      setIsOpen(true);
      if (!isCoolingDown(e164PhoneNumber)) sendCode({ phoneNumber: e164PhoneNumber, channel: 'sms' });
    },
    [isCoolingDown, sendCode]
  );

  const close = useCallback(() => setIsOpen(false), []);

  // The modal hands back the number it displays (human-formatted); the API needs E.164.
  const handleSendOtp = useCallback(async () => {
    sendCode({ phoneNumber, channel: 'sms' });
  }, [sendCode, phoneNumber]);

  const handleRequestCall = useCallback(() => {
    sendCode({ phoneNumber, channel: 'call' });
  }, [sendCode, phoneNumber]);

  // The API says after each send whether it would now take a call to this number. After a
  // failed send, a landline refusing SMS is the one case it does, and an unexplained failure
  // leaves the previous answer standing.
  const lastSentHere = sendOtp.lastSent?.phoneNumber === phoneNumber ? sendOtp.lastSent : null;
  const sendErrorKey = sendOtp.isError ? getOtpSendErrorKey(sendOtp.sendError) : null;
  const callAllowed =
    sendErrorKey === null
      ? !!lastSentHere?.callAvailable
      : sendOtp.variables?.phoneNumber === phoneNumber &&
        (sendErrorKey === 'smsUnsupported' || (sendErrorKey === 'sendFailed' && !!lastSentHere?.callAvailable));

  const handleVerifyOtp = useCallback(
    (_displayedPhoneNumber: string, code: string) => verifyCode({ phoneNumber, code }),
    [verifyCode, phoneNumber]
  );

  const handleChangeNumber = useCallback(() => {
    setIsOpen(false);
    // Wait for the dialog's focus trap to be torn down, or it pulls focus back in.
    requestAnimationFrame(() => onChangeNumber());
  }, [onChangeNumber]);

  const getVerifyErrorMessage = useCallback((error: unknown) => t(getOtpVerifyErrorKey(error)), [t]);

  const formatTime = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
    return (epochMs: number) => formatter.format(epochMs);
  }, [locale]);

  const modalProps: OtpVerificationFlowModalProps = {
    isOpen,
    onClose: close,
    onVerified,
    phoneNumber: formatPhoneNumberForDisplay(phoneNumber),
    onSendOtp: handleSendOtp,
    onVerifyOtp: handleVerifyOtp,
    translations,
    countdown: sendOtp.countdown,
    canResend: sendOtp.canResend,
    isSending: sendOtp.isPending,
    isVerifying: verifyOtp.isPending,
    delivery: getOtpDeliveryInfo({
      channel: sendOtp.channel,
      isSending: sendOtp.isPending,
      sendFailed: sendOtp.isError,
      sendId: sendOtp.sendId,
      status,
      statusUnavailable
    }),
    onChangeNumber: handleChangeNumber,
    call: { request: handleRequestCall, allowed: callAllowed },
    sendError: sendErrorKey ? t(sendErrorKey) : null,
    getVerifyErrorMessage,
    formatTime
  };

  return { isOpen, start, close, modalProps };
}
